import type { IFuseOptions } from 'fuse.js';

import type { CatalogItem } from '@/entities/catalog-item';

export interface FuzzySearchSettings {
  threshold: number;
  minMatchCharLength: number;
  ignoreLocation: boolean;
  location: number;
  distance: number;
  isCaseSensitive: boolean;
  ignoreDiacritics: boolean;
  useTokenSearch: boolean;
  tokenMatch: 'all' | 'any';
  ignoreFieldNorm: boolean;
  fieldNormWeight: number;
  findAllMatches: boolean;
  useExtendedSearch: boolean;
}

// Mirrors the permanent production defaults in catalogFuse.ts; everything else
// is the Fuse.js default.
export const DEFAULT_FUZZY_SETTINGS: FuzzySearchSettings = {
  threshold: 0.3,
  minMatchCharLength: 1,
  ignoreLocation: false,
  location: 0,
  distance: 100,
  isCaseSensitive: false,
  ignoreDiacritics: false,
  useTokenSearch: true,
  tokenMatch: 'all',
  ignoreFieldNorm: false,
  fieldNormWeight: 1,
  findAllMatches: false,
  useExtendedSearch: false
};

export type KnobControl =
  | { kind: 'switch' }
  | { kind: 'number'; min?: number; max?: number; step?: number }
  | { kind: 'select'; options: readonly string[] };

export interface KnobDef {
  key: keyof FuzzySearchSettings;
  label: string;
  description: string;
  group: string;
  control: KnobControl;
}

// Descriptions are plain-English forms of the Fuse.js option docs, written for a
// non-engineer tester.
export const KNOBS: KnobDef[] = [
  {
    key: 'threshold',
    label: 'Threshold',
    description:
      'How close a match must be. 0 = exact only, 1 = match almost anything. The biggest lever on how loose results are.',
    group: 'Match strictness',
    control: { kind: 'number', min: 0, max: 1, step: 0.05 }
  },
  {
    key: 'minMatchCharLength',
    label: 'Min match length',
    description:
      'Ignore matches shorter than this many characters. Raise above 1 to filter out trivial single-character hits.',
    group: 'Match strictness',
    control: { kind: 'number', min: 1, step: 1 }
  },
  {
    key: 'ignoreLocation',
    label: 'Ignore location',
    description:
      'When on, a term can match anywhere in the text with equal weight (position is ignored).',
    group: 'Match location',
    control: { kind: 'switch' }
  },
  {
    key: 'location',
    label: 'Location',
    description:
      'Where in the text the term is expected; matches near here rank better. Ignored when "Ignore location" is on.',
    group: 'Match location',
    control: { kind: 'number', min: 0, step: 1 }
  },
  {
    key: 'distance',
    label: 'Distance',
    description:
      'How far from the expected location a match may sit before it is penalised. Ignored when "Ignore location" is on.',
    group: 'Match location',
    control: { kind: 'number', min: 0, step: 10 }
  },
  {
    key: 'isCaseSensitive',
    label: 'Case sensitive',
    description: 'When on, capitals and lowercase letters are treated as different.',
    group: 'Text handling',
    control: { kind: 'switch' }
  },
  {
    key: 'ignoreDiacritics',
    label: 'Ignore diacritics',
    description: 'When on, accented characters match their plain equivalents (é = e).',
    group: 'Text handling',
    control: { kind: 'switch' }
  },
  {
    key: 'useTokenSearch',
    label: 'Token search',
    description: 'Split a multi-word query into words and match each with TF-IDF scoring.',
    group: 'Tokenization',
    control: { kind: 'switch' }
  },
  {
    key: 'tokenMatch',
    label: 'Token match',
    description:
      '"all" = every query word must match (narrows results); "any" = matching one word is enough.',
    group: 'Tokenization',
    control: { kind: 'select', options: ['all', 'any'] }
  },
  {
    key: 'ignoreFieldNorm',
    label: 'Ignore field-length norm',
    description:
      'When on, the relevance score ignores how long the matched Title is, so a shorter Title no longer ranks above a longer one for the same match.',
    group: 'Field-length scoring',
    control: { kind: 'switch' }
  },
  {
    key: 'fieldNormWeight',
    label: 'Field-norm weight',
    description:
      'How strongly the length of the matched Title affects ranking — higher values increase the advantage of shorter Titles. No effect when "Ignore field-length norm" is on.',
    group: 'Field-length scoring',
    control: { kind: 'number', min: 0, max: 3, step: 0.1 }
  },
  {
    key: 'findAllMatches',
    label: 'Find all matches',
    description: 'Keep scanning after a good match is found (mainly affects highlighting).',
    group: 'Advanced',
    control: { kind: 'switch' }
  },
  {
    key: 'useExtendedSearch',
    label: 'Extended search',
    description:
      "Enable Unix-like query operators: 'exact (exact), !exclude (inverse), ^prefix (starts-with), suffix$ (ends-with).",
    group: 'Advanced',
    control: { kind: 'switch' }
  }
];

export function toFuseOptions(settings: FuzzySearchSettings): IFuseOptions<CatalogItem> {
  return {
    keys: ['Title'],
    threshold: settings.threshold,
    minMatchCharLength: settings.minMatchCharLength,
    ignoreLocation: settings.ignoreLocation,
    location: settings.location,
    distance: settings.distance,
    isCaseSensitive: settings.isCaseSensitive,
    ignoreDiacritics: settings.ignoreDiacritics,
    useTokenSearch: settings.useTokenSearch,
    tokenMatch: settings.tokenMatch,
    ignoreFieldNorm: settings.ignoreFieldNorm,
    fieldNormWeight: settings.fieldNormWeight,
    findAllMatches: settings.findAllMatches,
    useExtendedSearch: settings.useExtendedSearch
  };
}
